import type { GridConfig } from '../types';

/**
 * CSS Helper Utility for DataGrid
 * 
 * Combines default CSS classes with custom classes from GridConfig
 * Supports both grid_css_class (legacy) and tableClasses (granular) configuration
 */

/**
 * Merge CSS classes with proper spacing
 */
export const mergeClasses = (...classes: (string | undefined)[]): string => {
  return classes.filter(Boolean).join(' ').trim();
};

/**
 * Default border classes for vertical column lines
 */
const defaultBorderClasses = {
  headerCell: '',
  groupHeaderCell: '',
  bodyCell: ''
};

/**
 * Get CSS classes for table elements based on GridConfig
 */
export const getTableClasses = (gridConfig: GridConfig) => {
  const headerCustomization = gridConfig.headerCustomization || {};

  // Get base classes based on style
  let baseClasses = getBaseTableClasses(headerCustomization.style || 'default');

  // Apply header customization
  const customizedClasses = applyHeaderCustomization(baseClasses, headerCustomization);

  // Override with custom table classes if provided
  const customClasses = gridConfig.tableClasses || {};

  // Merge custom classes with default border classes to ensure vertical lines are always visible
  const mergeWithBorders = (customClass: string | undefined, defaultClass: string, borderClass: string) => {
    if (!customClass) return defaultClass;
    // Check if custom class already has border-r, if not add default borders
    if (!customClass.includes('border-r')) {
      return mergeClasses(customClass, borderClass);
    }
    return customClass;
  };

  return {
    table: customClasses.table || customizedClasses.table,
    thead: customClasses.thead || customizedClasses.thead,
    tbody: customClasses.tbody || customizedClasses.tbody,
    headerRow: customClasses.headerRow || customizedClasses.headerRow,
    headerCell: mergeWithBorders(customClasses.headerCell, customizedClasses.headerCell, defaultBorderClasses.headerCell),
    groupHeaderCell: mergeWithBorders(customClasses.groupHeaderCell, customizedClasses.groupHeaderCell || customizedClasses.headerCell, defaultBorderClasses.groupHeaderCell),
    bodyRow: customClasses.bodyRow || customizedClasses.bodyRow,
    bodyCell: mergeWithBorders(customClasses.bodyCell, customizedClasses.bodyCell, defaultBorderClasses.bodyCell)
  };
};

const getBaseTableClasses = (style: string) => {
  switch (style) {
    case 'minimal':
      return tableStyleExamples.minimal;
    case 'corporate':
      return tableStyleExamples.corporate;
    case 'modern':
      return tableStyleExamples.modern;
    case 'dark':
      return tableStyleExamples.dark;
    case 'striped':
      return tableStyleExamples.striped;
    case 'bordered':
      return tableStyleExamples.bordered;
    case 'compact':
      return tableStyleExamples.compact;
    case 'spacious':
      return tableStyleExamples.spacious;
    default:
      return {
        table: 'min-w-full min-w-full',
        thead: 'sticky top-0 z-10',
        tbody: 'bg-white dark:bg-gray-900',
        headerRow: '',
        headerCell: 'px-2 sm:px-3 py-2.5 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider cursor-pointer bg-gray-50 dark:bg-gray-800 hover:bg-violet-50 dark:hover:bg-gray-600 transition-all duration-200 border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
        groupHeaderCell: 'px-2 sm:px-3 py-2.5 text-center text-sm font-bold text-gray-900 dark:text-gray-100 bg-gradient-to-r from-violet-50 to-violet-50 dark:from-violet-900/30 dark:to-violet-900/30 border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
        bodyRow: 'hover:bg-violet-50/50 dark:hover:bg-gray-700 cursor-pointer transition-colors',
        bodyCell: 'px-2 sm:px-3 py-1.5 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 border-l border-t border-gray-200 dark:border-gray-700'
      };
  }
};

const applyHeaderCustomization = (baseClasses: any, customization: any) => {
  const classes = { ...baseClasses };
  
  // Return base classes if customization is undefined or null
  if (!customization) {
    return classes;
  }
  
  // Apply header height
  if (customization.headerHeight) {
    const heightClasses = {
      compact: { 
        headerCell: classes.headerCell?.replace(/py-\d+/g, 'py-2'), 
        groupHeaderCell: classes.groupHeaderCell?.replace(/py-\d+/g, 'py-3') 
      },
      normal: { 
        headerCell: classes.headerCell?.replace(/py-\d+/g, 'py-4'), 
        groupHeaderCell: classes.groupHeaderCell?.replace(/py-\d+/g, 'py-5') 
      },
      spacious: { 
        headerCell: classes.headerCell?.replace(/py-\d+/g, 'py-6'), 
        groupHeaderCell: classes.groupHeaderCell?.replace(/py-\d+/g, 'py-7') 
      }
    };
    Object.assign(classes, heightClasses[customization.headerHeight]);
  }
  
  // Apply borders
  if (customization.showBorders === false) {
    classes.headerCell = classes.headerCell?.replace(/border-r[^ ]*/g, '').trim();
    classes.groupHeaderCell = classes.groupHeaderCell?.replace(/border-[^ ]*/g, '').trim();
  }
  
  // Apply shadows / header borders
  if (customization.showShadows === false) {
    classes.thead = classes.thead?.replace(/border-y[^ ]*/g, '').replace(/shadow-[^ ]*/g, '').trim();
    classes.groupHeaderCell = classes.groupHeaderCell?.replace(/shadow-[^ ]*/g, '').trim();
  }
  
  // Apply custom classes
  if (customization.customHeaderClasses) {
    classes.headerCell = `${classes.headerCell} ${customization.customHeaderClasses}`.trim();
  }
  
  if (customization.customGroupHeaderClasses) {
    classes.groupHeaderCell = `${classes.groupHeaderCell} ${customization.customGroupHeaderClasses}`.trim();
  }
  
  return classes;
};

/**
 * Get custom styles for header customization
 */
export const getHeaderCustomStyles = (customization: any) => {
  const styles: any = {};
  
  // Return empty styles if customization is undefined or null
  if (!customization) {
    return styles;
  }
  
  // Apply custom colors using CSS custom properties
  if (customization.headerTextColor) {
    styles['--header-text-color'] = customization.headerTextColor;
  }
  
  if (customization.headerBackgroundColor) {
    styles['--header-bg-color'] = customization.headerBackgroundColor;
  }
  
  if (customization.groupHeaderColor) {
    styles['--group-header-color'] = customization.groupHeaderColor;
  }
  
  if (customization.headerBorderColor) {
    styles['--header-border-color'] = customization.headerBorderColor;
  }
  
  if (customization.headerHoverBg) {
    styles['--header-hover-bg'] = customization.headerHoverBg;
  }
  
  if (customization.headerActiveBg) {
    styles['--header-active-bg'] = customization.headerActiveBg;
  }
  
  if (customization.headerFontSize) {
    styles['--header-font-size'] = customization.headerFontSize;
  }
  
  if (customization.headerFontWeight) {
    styles['--header-font-weight'] = customization.headerFontWeight;
  }
  
  if (customization.headerBorderRadius) {
    styles['--header-border-radius'] = customization.headerBorderRadius;
  }
  
  if (customization.headerPaddingX) {
    styles['--header-padding-x'] = customization.headerPaddingX;
  }
  
  if (customization.headerPaddingY) {
    styles['--header-padding-y'] = customization.headerPaddingY;
  }
  
  return styles;
};

/**
 * Get CSS classes with custom color support
 */
export const getHeaderClassesWithCustomColors = (baseClasses: string, customization: any) => {
  let classes = baseClasses;
  
  // Return base classes if customization is undefined or null
  if (!customization) {
    return classes;
  }
  
  // Add custom classes that will use CSS variables
  if (customization.headerTextColor) {
    classes += ' custom-header-text';
  }
  
  if (customization.headerBackgroundColor) {
    classes += ' custom-header-bg';
  }
  
  if (customization.groupHeaderColor) {
    classes += ' custom-group-header-bg';
  }
  
  if (customization.headerBorderColor) {
    classes += ' custom-header-border';
  }
  
  if (customization.headerHoverBg) {
    classes += ' custom-header-hover';
  }
  
  if (customization.headerActiveBg) {
    classes += ' custom-header-active';
  }
  
  if (customization.headerFontSize) {
    classes += ' custom-header-font-size';
  }
  
  if (customization.headerFontWeight) {
    classes += ' custom-header-font-weight';
  }
  
  if (customization.headerBorderRadius) {
    classes += ' custom-header-border-radius';
  }
  
  if (customization.headerPaddingX || customization.headerPaddingY) {
    classes += ' custom-header-padding';
  }
  
  return classes.trim();
};

/**
 * Apply conditional CSS classes based on column configuration
 */
export const getColumnClasses = (
  baseClasses: string,
  columnConfig?: any,
  isGroupable?: boolean
): string => {
  const classes = [baseClasses];
  
  if (columnConfig?.column_css_class) {
    classes.push(columnConfig.column_css_class);
  }
  
  if (isGroupable) {
    classes.push('draggable-column');
  }
  
  return mergeClasses(...classes);
};

/**
 * Get alignment classes based on column configuration
 */
export const getAlignmentClasses = (alignment?: 'left' | 'center' | 'right'): string => {
  switch (alignment) {
    case 'center':
      return 'text-center';
    case 'right':
      return 'text-right';
    default:
      return 'text-left';
  }
};

/**
 * CSS class examples for common table styling patterns
 */
export const tableStyleExamples = {
  // Modern minimal style
  minimal: {
    table: 'min-w-full min-w-full',
    thead: 'bg-white dark:bg-gray-800 sticky top-0 z-10',
    tbody: 'bg-white dark:bg-gray-900',
    headerRow: '',
    headerCell: 'px-2 sm:px-3 py-2.5 text-left text-sm font-medium text-gray-900 dark:text-gray-100 border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    groupHeaderCell: 'px-2 sm:px-3 py-2.5 text-center text-sm font-semibold text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-700 border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    bodyRow: 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors',
    bodyCell: 'px-2 sm:px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 border-l border-t border-gray-200 dark:border-gray-700'
  },

  // Professional corporate style
  corporate: {
    table: 'min-w-full min-w-full',
    thead: 'bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 sticky top-0 z-10',
    tbody: 'bg-white dark:bg-gray-900',
    headerRow: '',
    headerCell: 'px-2 sm:px-3 py-2.5 text-left text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    groupHeaderCell: 'px-2 sm:px-3 py-2.5 text-center text-sm font-bold text-slate-900 dark:text-slate-100 bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    bodyRow: 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors',
    bodyCell: 'px-2 sm:px-3 py-1.5 text-sm text-slate-700 dark:text-slate-300 border-l border-t border-gray-200 dark:border-gray-700'
  },

  // Modern tech style
  modern: {
    table: 'min-w-full min-w-full',
    // The header is sticky, so it MUST be opaque — a transparent header lets the
    // rows scrolling underneath show through and collide with the column labels.
    // The background goes on the cells (not just <thead>) because a row-group
    // background is not reliably painted at the sticky offset.
    thead: 'bg-violet-50 dark:bg-gray-800 sticky top-0 z-10',
    tbody: 'bg-white dark:bg-gray-900',
    headerRow: '',
    headerCell: 'px-2 sm:px-3 py-2.5 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider bg-violet-50 dark:bg-gray-800 border-l border-t border-gray-200 dark:border-gray-600 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors overflow-hidden',
    groupHeaderCell: 'px-2 sm:px-3 py-2.5 text-center text-sm font-bold text-gray-800 dark:text-gray-100 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/30 border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    bodyRow: 'hover:bg-gray-50/80 dark:hover:bg-gray-700 cursor-pointer transition-colors',
    bodyCell: 'px-2 sm:px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 border-l border-t border-gray-200 dark:border-gray-700'
  },

  // Dark theme
  dark: {
    table: 'min-w-full min-w-full',
    thead: 'bg-gray-800 sticky top-0 z-10',
    tbody: 'bg-gray-900',
    headerRow: '',
    headerCell: 'px-2 sm:px-3 py-2.5 text-left text-xs font-medium text-gray-300 uppercase tracking-wider border-l border-t border-gray-600 overflow-hidden',
    groupHeaderCell: 'px-2 sm:px-3 py-2.5 text-center text-sm font-bold text-gray-200 bg-gray-700 border-l border-t border-gray-600 overflow-hidden',
    bodyRow: 'hover:bg-gray-700 cursor-pointer transition-colors',
    bodyCell: 'px-2 sm:px-3 py-1.5 whitespace-nowrap text-sm text-white border-l border-t border-gray-700'
  },

  // Striped rows
  striped: {
    table: 'min-w-full min-w-full',
    thead: 'bg-white dark:bg-gray-800 sticky top-0 z-10',
    headerRow: '',
    headerCell: 'px-2 sm:px-3 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    groupHeaderCell: 'px-2 sm:px-3 py-2.5 text-center text-sm font-bold text-gray-800 dark:text-gray-200 border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    tbody: 'bg-white dark:bg-gray-900',
    bodyRow: 'even:bg-gray-50/60 dark:even:bg-gray-800 hover:bg-violet-50 dark:hover:bg-gray-700 cursor-pointer transition-colors',
    bodyCell: 'px-2 sm:px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 border-l border-t border-gray-200 dark:border-gray-700'
  },

  // Bordered style
  bordered: {
    table: 'min-w-full',
    thead: 'bg-white dark:bg-gray-800 sticky top-0 z-10',
    headerRow: '',
    headerCell: 'px-2 sm:px-3 py-2.5 border-l border-t border-gray-200 dark:border-gray-600 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase overflow-hidden',
    groupHeaderCell: 'px-2 sm:px-3 py-2.5 text-center text-sm font-bold text-gray-800 dark:text-gray-200 border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    bodyRow: 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors',
    bodyCell: 'px-2 sm:px-3 py-1.5 border-l border-t border-gray-200 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100'
  },

  // Compact style
  compact: {
    table: 'min-w-full min-w-full',
    thead: 'bg-white dark:bg-gray-800 sticky top-0 z-10',
    tbody: 'bg-white dark:bg-gray-900',
    headerRow: '',
    headerCell: 'px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    groupHeaderCell: 'px-2 py-2 text-center text-sm font-bold text-gray-800 dark:text-gray-200 border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    bodyRow: 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors',
    bodyCell: 'px-2 py-0.5 text-sm text-gray-900 dark:text-gray-100 border-l border-t border-gray-200 dark:border-gray-700'
  },

  // Large spacing
  spacious: {
    table: 'min-w-full min-w-full',
    thead: 'bg-white dark:bg-gray-800 sticky top-0 z-10',
    tbody: 'bg-white dark:bg-gray-900',
    headerRow: '',
    headerCell: 'px-4 sm:px-6 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    groupHeaderCell: 'px-4 sm:px-6 py-3 text-center text-sm font-bold text-gray-800 dark:text-gray-200 border-l border-t border-gray-200 dark:border-gray-600 overflow-hidden',
    bodyRow: 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors',
    bodyCell: 'px-4 sm:px-6 py-1.5 text-base text-gray-900 dark:text-gray-100 border-l border-t border-gray-200 dark:border-gray-700'
  }
};
